import { useEffect, useSyncExternalStore } from 'react'

export type Theme = 'light' | 'dark' | 'system'
const STORAGE_KEY = 'ops-console.theme'
const listeners = new Set<() => void>()
let theme: Theme = (() => {
  if (typeof window === 'undefined') return 'system'
  const saved = window.localStorage.getItem(STORAGE_KEY)
  return saved === 'light' || saved === 'dark' || saved === 'system' ? saved : 'system'
})()

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function resolved(next: Theme) {
  if (next !== 'system') return next
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function apply(next: Theme) {
  theme = next
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, next)
    document.documentElement.classList.toggle('dark', resolved(next) === 'dark')
  }
  listeners.forEach((listener) => listener())
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const current = useSyncExternalStore(subscribe, () => theme, () => 'system' as Theme)
  useEffect(() => {
    apply(current)
    if (current !== 'system') return
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => {
      document.documentElement.classList.toggle('dark', media.matches)
      listeners.forEach((listener) => listener())
    }
    media.addEventListener?.('change', onChange)
    return () => media.removeEventListener?.('change', onChange)
  }, [current])
  return <>{children}</>
}

export function useTheme() {
  const current = useSyncExternalStore(subscribe, () => theme, () => 'system' as Theme)
  return { theme: current, setTheme: apply }
}
